// The page editor's analytics (shown in the Analytics side panel): totals for
// the last 30 days, a 14-day chart and the top traffic sources. Days are US
// Eastern days, counted by the server.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PageDay, PageSource, PageStats } from '../api';
import { useEditorApi } from './editorApi';

/** "1,204". */
const count = (n: number | null | undefined) => new Intl.NumberFormat('en-US').format(Math.max(0, Math.round(Number(n) || 0)));

/** Conversion as the server counts it: leads per visitor, a whole percent
 *  (worked out here only when the server sent none). */
export function conversionText(stats: Pick<PageStats, 'conversion' | 'leads' | 'visitors'> | null): string {
  if (!stats) return '0%';
  const sent = Number(stats.conversion);
  const pct = Number.isFinite(sent) ? sent : stats.visitors > 0 ? (stats.leads / stats.visitors) * 100 : 0;
  return `${Math.max(0, Math.min(100, Math.round(pct)))}%`;
}

/** "1 view", "12 views". */
const plural = (n: number, word: string) => `${count(n)} ${word}${Math.round(Number(n) || 0) === 1 ? '' : 's'}`;

/** "Oct 9" for a 'YYYY-MM-DD' day (a calendar date, no time zone shift). */
function dayLabel(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return day;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)));
}

export function PageAnalyticsTab({ pageId, hasForm, published }: { pageId: string; hasForm: boolean; published: boolean }) {
  const api = useEditorApi();
  const [stats, setStats] = useState<PageStats | null>(null);
  const [sources, setSources] = useState<PageSource[]>([]);
  const [daily, setDaily] = useState<PageDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const req = useRef(0);

  const load = useCallback(async () => {
    const n = ++req.current;
    setLoading(true);
    setError(null);
    try {
      const [s, src, d] = await Promise.all([api.pageStats(pageId, 30), api.pageSources(pageId, 30), api.pageDaily(pageId, 14)]);
      if (n !== req.current) return;
      setStats(s); setSources(Array.isArray(src) ? src : []); setDaily(Array.isArray(d) ? d : []);
    } catch (e) {
      if (n === req.current) setError((e as Error).message || 'Couldn’t load the numbers. Try again.');
    } finally {
      if (n === req.current) setLoading(false);
    }
  }, [api, pageId]);
  useEffect(() => { void load(); }, [load]);

  return (
    <div className="grid gap-5" data-page-analytics>
      <section className="grid gap-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-text">Last 30 days</h3>
          <button type="button" className="btn-ghost btn-sm" disabled={loading} onClick={() => { void load(); }}>{loading ? 'Refreshing…' : 'Refresh'}</button>
        </div>
        {!published && <p className="text-xs text-textDim">Publish the page to start collecting numbers.</p>}
        {error && <p role="alert" className="text-xs text-danger">{error}</p>}
        <div className="grid grid-cols-6 gap-2">
          <Stat className="col-span-2" label="Views" value={count(stats?.views)} />
          <Stat className="col-span-2" label="Visitors" value={count(stats?.visitors)} />
          <Stat className="col-span-2" label="Clicks" value={count(stats?.clicks)} />
          <Stat className="col-span-3" label="Leads" value={count(stats?.leads)} />
          <Stat className="col-span-3" label="Conversion" value={conversionText(stats)} />
          <Stat className="col-span-3" label="From Google Ads" value={count(stats?.google_leads)} />
          <Stat className="col-span-3" label="Booked a call" value={count(stats?.booked)} />
        </div>
        <p className="text-xs text-textDim">
          Conversion is leads per visitor. From Google Ads counts leads that came with an ad click, kept for 90 days.{!hasForm && ' Add a form to the page to collect leads.'}
        </p>
      </section>

      <section className="grid gap-3 border-t border-border pt-5">
        <h3 className="text-sm font-semibold text-text">Last 14 days</h3>
        <DailyChart data={daily} />
      </section>

      <section className="grid gap-3 border-t border-border pt-5">
        <h3 className="text-sm font-semibold text-text">Top sources</h3>
        {sources.length === 0 ? (
          <p className="text-sm text-textDim">No visits yet.</p>
        ) : (
          <div className="grid gap-2" data-sources>
            {sources.map((s) => {
              const max = Math.max(...sources.map((x) => x.views), 1);
              return (
                <div key={s.source} className="grid grid-cols-[1fr_auto] items-center gap-2">
                  <div className="grid min-w-0 gap-1">
                    <div className="truncate text-sm text-text">{s.source === 'direct' ? 'Direct' : s.source}</div>
                    <div className="h-1.5 rounded bg-primary" style={{ width: `${Math.max(6, (s.views / max) * 100)}%` }} />
                  </div>
                  <span className="tnum text-sm text-textDim">{count(s.views)}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function DailyChart({ data }: { data: PageDay[] }) {
  if (!data.length) return <p className="text-sm text-textDim">No data yet.</p>;
  const max = Math.max(...data.map((d) => d.views), 1);
  return (
    <div className="flex items-end gap-1" style={{ height: 96 }} data-daily-chart>
      {data.map((d) => (
        <div key={d.day} className="flex min-w-0 flex-1 flex-col items-center justify-end"
          title={`${dayLabel(d.day)}: ${plural(d.views, 'view')}, ${plural(d.clicks, 'click')}, ${plural(d.leads, 'lead')}`}>
          <div className="w-full rounded-t bg-primary" style={{ height: `${Math.round((d.views / max) * 84)}px`, minHeight: d.views ? 3 : 0 }} />
          <span className="mt-1 text-[10px] text-textDim">{d.day.slice(8).replace(/^0/, '')}</span>
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value, className = '' }: { label: string; value: number | string; className?: string }) {
  return (
    <div className={`grid min-w-0 gap-0.5 rounded-lg border border-border px-3 py-2.5 ${className}`}>
      <div className="truncate text-xs text-textDim">{label}</div>
      <div className="tnum text-xl font-semibold text-text">{value}</div>
    </div>
  );
}
